import { useEffect } from "react";
import {
  Controller,
  Resolver,
  useForm,
  UseFormSetError,
} from "react-hook-form";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { AutoComplete } from "../../../../../../../components/core/AutoComplete/AutoComplete";
import { Divider } from "../../../../../../../components/core/Divider/Divider";
import { FormDataTech } from "../CollaboratorTechSkillsModal/CollaboratorTechSkillsModal";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import {
  SUB_COMPETENCE_LABEL,
  DESCRIPTION_LABEL,
  IS_REQUIRED_LABEL,
  LEVEL_LABEL,
  SCOPE_LABEL,
  CORE_COMPETENCE_LABEL,
  CATEGORY_LABEL,
} from "../../../../../../../constants/Dictionary";
import TextArea from "../../../../../../../components/core/TextArea/TextArea";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TechSkill } from "../../../../../../../types/api/techSkills.type";
import {
  useCompetencesInfinite,
  useComponentsInfinite,
  useStreamsInfinite,
  useSystemSoftwaresInfinite,
} from "../../../../../../../hooks/professionalHistory.hook";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import { CHARACTER_MAX_LENGTH } from "../../../../../../../constants/AppConstants";

type TechSkillFormProps = {
  formID: string;
  onSubmit: (
    data: FormDataTech,
    setError: UseFormSetError<FormDataTech>,
  ) => void;
  showLevel?: boolean;
  record?: TechSkill & { level?: string };
  setIsDirty: (isDirty: boolean) => void;
  isLevelRequired?: boolean;
};

export const schema = (isLevelRequired?: boolean) =>
  yup
    .object({
      competence: yup
        .string()
        .required(`${CATEGORY_LABEL} ${IS_REQUIRED_LABEL}`),
      stream: yup.string().required(`${SCOPE_LABEL} ${IS_REQUIRED_LABEL}`),
      software: yup
        .string()
        .required(`${CORE_COMPETENCE_LABEL} ${IS_REQUIRED_LABEL}`),
      component: yup.string(),
      description: yup.string(),
      level: isLevelRequired
        ? yup
            .string()
            .nullable()
            .required(`${LEVEL_LABEL} ${IS_REQUIRED_LABEL}`)
        : yup.string().nullable().optional(),
    })
    .required();

export const TechSkillForm = ({
  formID,
  onSubmit,
  showLevel = false,
  record,
  setIsDirty,
  isLevelRequired,
}: TechSkillFormProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);

  const {
    handleSubmit,
    reset,
    control,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormDataTech>({
    resolver: yupResolver(schema(isLevelRequired)) as Resolver<FormDataTech>,
  });

  useEffect(() => {
    if (record) {
      reset({
        competence: record.competence ?? "",
        stream: record.stream ?? "",
        software: record.software ?? "",
        component: record.component ?? "",
        description: record.description ?? "",
        level: record.level ?? null,
      });
    } else {
      reset({
        competence: "",
        stream: "",
        software: "",
        component: "",
        description: "",
        level: null,
      });
    }
  }, [record]);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  const {
    data: competencesOptions,
    initLoading: competencesInitLoading,
    canLoadMore: loadMoreCompetences,
    fetchMore: fetchMoreCompetences,
    clearSearch: clearCompetencesSearch,
    setSearch: setCompetencesSearchValue,
    fetchingMore: fetchingMoreCompetences,
  } = useCompetencesInfinite();

  const {
    data: streamsOptions,
    initLoading: streamsInitLoading,
    canLoadMore: loadMoreStreams,
    fetchMore: fetchStreams,
    setSearch: setStreamsSearchValue,
    clearSearch: clearStreamsSearch,
    fetchingMore: fetchingMoreStreams,
  } = useStreamsInfinite();

  const {
    data: systemSoftwaresOptions,
    initLoading: systemSoftwaresInitLoading,
    canLoadMore: loadMoreSystemSoftwares,
    fetchMore: fetchSystemSoftwares,
    setSearch: setSystemSoftwaresSearchValue,
    clearSearch: clearSystemSoftwaresSearch,
    fetchingMore: fetchingMoreSystemSoftwares,
  } = useSystemSoftwaresInfinite();

  const {
    data: componentsOptions,
    initLoading: componentsInitLoading,
    canLoadMore: loadMoreComponents,
    fetchMore: fetchComponents,
    setSearch: setComponentsSearchValue,
    clearSearch: clearComponentsSearch,
    fetchingMore: fetchingMoreComponents,
  } = useComponentsInfinite();

  return (
    <form
      id={formID}
      onSubmit={handleSubmit((data) => onSubmit(data, setError))}
    >
      <div className="form-row">
        <FormField error={errors.competence?.message}>
          <Controller
            name="competence"
            control={control}
            render={({ field: { value, onChange } }) => (
              <AutoComplete
                label={CATEGORY_LABEL}
                options={competencesOptions.map((competence) => ({
                  value: competence,
                  label: competence,
                }))}
                onClear={clearCompetencesSearch}
                loading={competencesInitLoading}
                fetchMore={fetchMoreCompetences}
                canLoadMore={loadMoreCompetences}
                value={value}
                onChange={(value) => {
                  onChange(value.toUpperCase());
                  setCompetencesSearchValue(value);
                }}
                error={errors.competence?.message}
                fetchingMore={fetchingMoreCompetences}
              />
            )}
          />
        </FormField>
        <FormField error={errors.stream?.message}>
          <Controller
            name="stream"
            control={control}
            render={({ field: { value, onChange } }) => (
              <AutoComplete
                label={SCOPE_LABEL}
                options={streamsOptions.map((stream) => ({
                  value: stream,
                  label: stream,
                }))}
                onClear={clearStreamsSearch}
                loading={streamsInitLoading}
                fetchMore={fetchStreams}
                canLoadMore={loadMoreStreams}
                value={value}
                onChange={(value) => {
                  onChange(value.toUpperCase());
                  setStreamsSearchValue(value);
                }}
                error={errors.stream?.message}
                fetchingMore={fetchingMoreStreams}
              />
            )}
          />
        </FormField>
        <FormField error={errors?.software?.message}>
          <Controller
            name="software"
            control={control}
            render={({ field: { value, onChange } }) => (
              <AutoComplete
                label={CORE_COMPETENCE_LABEL}
                options={systemSoftwaresOptions.map((software) => ({
                  value: software,
                  label: software,
                }))}
                onClear={clearSystemSoftwaresSearch}
                loading={systemSoftwaresInitLoading}
                fetchMore={fetchSystemSoftwares}
                canLoadMore={loadMoreSystemSoftwares}
                value={value}
                onChange={(value) => {
                  onChange(value.toUpperCase());
                  setSystemSoftwaresSearchValue(value);
                }}
                error={errors.software?.message}
                fetchingMore={fetchingMoreSystemSoftwares}
              />
            )}
          />
        </FormField>
        <FormField error={errors.component?.message}>
          <Controller
            name="component"
            control={control}
            render={({ field: { value, onChange } }) => (
              <AutoComplete
                label={SUB_COMPETENCE_LABEL}
                options={componentsOptions.map((component) => ({
                  value: component,
                  label: component,
                }))}
                onClear={clearComponentsSearch}
                loading={componentsInitLoading}
                fetchMore={fetchComponents}
                canLoadMore={loadMoreComponents}
                value={value}
                onChange={(value) => {
                  onChange(value.toUpperCase());
                  setComponentsSearchValue(value);
                }}
                error={errors.component?.message}
                fetchingMore={fetchingMoreComponents}
              />
            )}
          />
        </FormField>
      </div>
      <div className="form-row">
        <FormField error={errors.description?.message}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                label={DESCRIPTION_LABEL}
                maxLength={CHARACTER_MAX_LENGTH}
                {...field}
              />
            )}
          />
        </FormField>
      </div>
      {showLevel && (
        <>
          <Divider />
          <div className="form-row-level">
            <FormField error={errors.level?.message}>
              <Controller
                name="level"
                control={control}
                render={({ field }) => (
                  <Select
                    variant={SelectVariantEnum.DEFAULT}
                    label={LEVEL_LABEL}
                    options={orgSettings.techSkills.levels}
                    id="level-select"
                    {...field}
                  />
                )}
              />
            </FormField>
          </div>
        </>
      )}
    </form>
  );
};
